// src/components/forms/shared/AddressSection.tsx
// Componente reutilizable para direcciones (facturación y envío)
// Usado por: OrderForm y CotizacionForm

import React from 'react'
import type { Departamento, Ciudad } from '../../../types/orderFormTypes'
import { Input, Select } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { MapPinIcon, TruckIcon } from '@heroicons/react/24/outline'

interface AddressSectionProps {
  // Datos de direcciones
  billingAddress: {
    address_1?: string
    address_2?: string
    city?: string
    state?: string
    phone?: string
  }
  shippingAddress: {
    address_1?: string
    address_2?: string
    city?: string
    state?: string
    phone?: string
  }
  
  // Handlers
  onBillingAddressChange: (field: string, value: string) => void
  onShippingAddressChange: (field: string, value: string) => void
  
  // Configuración
  mode: 'view' | 'edit' | 'create'
  fieldsLocked?: boolean
  departamentos: Departamento[]
  ciudades: Ciudad[]
  errors: { [key: string]: string }
  
  // Funciones de renderizado
  renderDepartamentoOptions: (addressType: 'billing' | 'shipping') => React.ReactNode
  renderCiudadOptions: (addressType: 'billing' | 'shipping') => React.ReactNode
  
  // Si recoge en bodega, direcciones autocompletadas (solo lectura)
  recogeEnBodega?: boolean
  
  // Ocultar dirección 2 (para CotizacionForm que no usa address_2)
  showAddress2?: boolean
}

export const AddressSection: React.FC<AddressSectionProps> = ({
  billingAddress,
  shippingAddress,
  onBillingAddressChange,
  onShippingAddressChange,
  mode,
  fieldsLocked = false,
  departamentos,
  ciudades,
  errors,
  renderDepartamentoOptions,
  renderCiudadOptions,
  recogeEnBodega = false,
  showAddress2 = true
}) => {
  // Direcciones se bloquean solo con recoge ON (autocompletadas por bodega)
  const isReadOnly = mode === 'view' || recogeEnBodega || fieldsLocked
  
  // Convertir opciones renderizadas a formato Select
  const getSelectOptions = (renderFunction: (addressType: 'billing' | 'shipping') => React.ReactNode, addressType: 'billing' | 'shipping'): Array<{ value: string | number; label: string; disabled?: boolean }> => {
    try {
      const rendered = renderFunction(addressType)
      if (Array.isArray(rendered)) {
        const options: Array<{ value: string | number; label: string; disabled?: boolean }> = []
        
        rendered.forEach((option: any) => {
          // Manejar elementos React
          if (option && typeof option === 'object') {
            // Si es un elemento React con props
            if (option.props) {
              const value = option.props.value || ''
              const children = option.props.children
              // Si children es un string, usarlo como label
              const label = typeof children === 'string' 
                ? children 
                : (Array.isArray(children) ? children.join('') : value || '')
              
              if (value !== undefined) {
                options.push({
                  value,
                  label: label || value,
                  disabled: option.props.disabled || false
                })
              }
            } else if (option.value !== undefined) {
              // Si es un objeto plano
              options.push({
                value: option.value,
                label: option.label || option.value,
                disabled: option.disabled || false
              })
            }
          }
        })
        
        return options.length > 0 ? options : [{ value: '', label: 'Seleccione' }]
      }
      // Si no es array, retornar opción por defecto
      return [{ value: '', label: 'Seleccione' }]
    } catch (error) {
      console.error('Error al convertir opciones:', error)
      return [{ value: '', label: 'Seleccione' }]
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Dirección de Facturación */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <SectionTitle icon={MapPinIcon}>Dirección de Facturación</SectionTitle>
        <div className="space-y-3">
          <div className={showAddress2 ? "grid grid-cols-2 gap-4" : ""}>
            {/* Dirección 1 */}
            <FormField
              label="Dirección"
              required
              error={errors.billing_address}
            >
              <Input
                type="text" 
                value={billingAddress.address_1 || ''} 
                onChange={(e) => onBillingAddressChange('address_1', e.target.value)}
                readOnly={isReadOnly}
                error={!!errors.billing_address}
                errorMessage={errors.billing_address}
                mode={mode}
              />
            </FormField>
            
            {/* Dirección 2 - Solo mostrar si showAddress2 es true */}
            {showAddress2 && (
              <FormField
                label="Dirección 2"
              >
                <Input
                type="text" 
                value={billingAddress.address_2 || ''} 
                onChange={(e) => onBillingAddressChange('address_2', e.target.value)}
                readOnly={isReadOnly}
                  mode={mode}
              />
              </FormField>
            )}
            </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Departamento */}
            <FormField
              label="Departamento"
              required
              error={errors.billing_state}
            >
              <Select
                value={billingAddress.state || ''}
                onChange={(e) => onBillingAddressChange('state', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderDepartamentoOptions, 'billing')}
                placeholder="Seleccione"
                error={!!errors.billing_state}
                errorMessage={errors.billing_state}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
            
            {/* Ciudad */}
            <FormField
              label="Ciudad"
              required
              error={errors.billing_city}
            >
              <Select
                value={billingAddress.city || ''}
                onChange={(e) => onBillingAddressChange('city', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderCiudadOptions, 'billing')}
                placeholder="Seleccione"
                error={!!errors.billing_city}
                errorMessage={errors.billing_city}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Dirección de Envío */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <SectionTitle icon={TruckIcon}>Dirección de Envío</SectionTitle>
        <div className="space-y-3">
          <div className={showAddress2 ? "grid grid-cols-2 gap-4" : ""}>
            {/* Dirección 1 */}
            <FormField
              label="Dirección"
              required
              error={errors.shipping_address}
            >
              <Input
                type="text" 
                value={shippingAddress.address_1 || ''} 
                onChange={(e) => onShippingAddressChange('address_1', e.target.value)}
                readOnly={isReadOnly}
                error={!!errors.shipping_address}
                errorMessage={errors.shipping_address}
                mode={mode}
              />
            </FormField>
            
            {/* Dirección 2 - Solo mostrar si showAddress2 es true */}
            {showAddress2 && (
              <FormField
                label="Dirección 2"
              >
                <Input
                type="text" 
                value={shippingAddress.address_2 || ''} 
                onChange={(e) => onShippingAddressChange('address_2', e.target.value)}
                readOnly={isReadOnly}
                  mode={mode}
              />
              </FormField>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Departamento */}
            <FormField
              label="Departamento"
              required
              error={errors.shipping_state}
            >
              <Select
                value={shippingAddress.state || ''}
                onChange={(e) => onShippingAddressChange('state', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderDepartamentoOptions, 'shipping')}
                placeholder="Seleccione"
                error={!!errors.shipping_state}
                errorMessage={errors.shipping_state}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
            
            {/* Ciudad */}
            <FormField
              label="Ciudad"
              required
              error={errors.shipping_city}
            >
              <Select
                value={shippingAddress.city || ''}
                onChange={(e) => onShippingAddressChange('city', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderCiudadOptions, 'shipping')}
                placeholder="Seleccione"
                error={!!errors.shipping_city}
                errorMessage={errors.shipping_city}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
          </div>
        </div>
      </div>
    </div>
  )
}
